import {
  type PayloadAction,
  createAsyncThunk,
  createSlice
} from '@reduxjs/toolkit';

import { type IOfferRespondRequestApi, requestsApi } from '@/api/requests.api';
import {
  mapApiRequestToCard,
  mapApiRequestToLeasingRequest
} from '@/pages/Requests/services/map-api-request';
import type { IRequest } from '@/pages/Requests/types/request.types';
import { getNextLeasingRequestStage } from '@/shared/services/leasing-request-stage';
import { keysToCamelCase } from '@/shared/services/tools';
import type { ILeasingRequest } from '@/shared/types/leasing-request.types';
import type { TRequestApi } from '@/shared/types/requests-api.types';

const REQUESTS_LIST_FETCH_LIMIT = 50;

export const getRequestsList = createAsyncThunk(
  'requests/getRequestsList',
  async () => {
    return await requestsApi.getRequests({
      limit: REQUESTS_LIST_FETCH_LIMIT,
      offset: 0
    });
  }
);

export const getRequestDetail = createAsyncThunk(
  'requests/getRequestDetail',
  async (requestUuid: string) => {
    return await requestsApi.getRequestByUuid(requestUuid);
  }
);

export const respondToOffer = createAsyncThunk(
  'requests/respondToOffer',
  async (
    {
      requestUuid,
      body
    }: { requestUuid: string; body: IOfferRespondRequestApi },
    { dispatch }
  ) => {
    await requestsApi.respondToOffer(body);
    await dispatch(getRequestDetail(requestUuid));
  }
);

interface IRequestsState {
  items: ILeasingRequest[];
  requestCards: IRequest[];
  requestsList: TRequestApi[];
  isListLoading: boolean;
  detailRequest: ILeasingRequest | null;
  isDetailLoading: boolean;
}

const initialState: IRequestsState = {
  items: [],
  requestCards: [],
  requestsList: [],
  isListLoading: false,
  detailRequest: null,
  isDetailLoading: false
};

const requestsSlice = createSlice({
  name: 'requests',
  initialState,
  reducers: {
    resetRequestDetail: (state) => {
      state.detailRequest = null;
      state.isDetailLoading = false;
    },
    addLeasingRequest: (state, action: PayloadAction<ILeasingRequest>) => {
      state.items.unshift(action.payload);
    },
    advanceLeasingRequestStage: (state, action: PayloadAction<string>) => {
      const request = state.items.find((item) => item.id === action.payload);

      if (request) {
        request.stage = getNextLeasingRequestStage(request.stage);
      }

      if (state.detailRequest?.id === action.payload) {
        state.detailRequest.stage = getNextLeasingRequestStage(
          state.detailRequest.stage
        );
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRequestsList.pending, (state) => {
        state.isListLoading = true;
      })
      .addCase(getRequestsList.fulfilled, (state, action) => {
        state.isListLoading = false;
        state.requestsList = keysToCamelCase(action.payload).data;
        state.requestCards = state.requestsList.map((request) =>
          mapApiRequestToCard(request)
        );
      })
      .addCase(getRequestsList.rejected, (state) => {
        state.isListLoading = false;
      })
      .addCase(getRequestDetail.pending, (state) => {
        if (!state.detailRequest) {
          state.isDetailLoading = true;
        }
      })
      .addCase(getRequestDetail.fulfilled, (state, action) => {
        state.isDetailLoading = false;
        state.detailRequest = mapApiRequestToLeasingRequest(
          keysToCamelCase(action.payload)
        );
      })
      .addCase(getRequestDetail.rejected, (state) => {
        state.isDetailLoading = false;
        state.detailRequest = null;
      });
  }
});

export const {
  resetRequestDetail,
  addLeasingRequest,
  advanceLeasingRequestStage
} = requestsSlice.actions;

export default requestsSlice;
